import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { walletApi, dispensaries as dApi, billingApi } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Wallet as WalletIcon, Plus, ArrowDown, ArrowUp, CreditCard } from "lucide-react";

export default function Wallet() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [dispensary, setDispensary] = useState(null);
  const [data, setData] = useState(null);
  const [busyPkg, setBusyPkg] = useState("");

  useEffect(() => {
    if (!user) return;
    (async () => {
      let d = user.dispensary_id ? await dApi.get(user.dispensary_id).catch(() => null) : null;
      if (!d) {
        const all = await dApi.list();
        d = all.find((x) => x.owner_user_id === user.id) || all[0];
      }
      if (!d) return;
      setDispensary(d);
      const w = await walletApi.state(d.id);
      setData(w);
    })();
  }, [user]);

  if (loading) return <div className="min-h-screen"><NavBar /><div className="text-center py-20">Loading...</div></div>;
  if (!user) return <Navigate to="/auth?mode=login" replace />;
  if (user.role !== "dispensary" && user.role !== "admin") return <div className="min-h-screen"><NavBar /><div className="max-w-2xl mx-auto py-32 text-center"><h2 className="font-display text-4xl font-extrabold">Shop owners only</h2></div></div>;

  const stripeTopup = async (pkgId) => {
    if (!dispensary) return;
    setBusyPkg(pkgId);
    try {
      const r = await billingApi.checkout(pkgId, dispensary.id);
      if (r?.url) window.location.href = r.url;
      else toast.error("Couldn't start checkout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setBusyPkg(""); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 380, height: 380, background: "#FFE74C", top: -120, right: -80 }} />
      <NavBar />
      <Toaster position="top-right" theme="light" richColors />
      <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12 relative">
        <span className="sticker sticker-lemon mb-3 inline-block"><WalletIcon size={11} strokeWidth={2.4} /> Wallet</span>
        <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight leading-[0.92] mt-3 mb-2">{dispensary?.name}</h1>
        <p className="text-[#0B0F0C]/60 mb-8">Prepaid balance. Auto-deducts on each verified redemption. Auto-pauses deals at $0.</p>

        <div className="grid lg:grid-cols-3 gap-5 mb-8">
          <div className="lg:col-span-2 card-x card-green bg-[#15FF73] p-7">
            <div className="label-overline text-[#0B0F0C]">Current balance</div>
            <div className="font-display text-7xl font-extrabold mt-2 leading-none">${(data?.balance ?? 0).toFixed(2)}</div>
            <div className="mt-4 flex items-center gap-3 text-xs font-mono-x font-bold">
              <span className="px-2 py-1 rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8]">Rate: {data?.commission_rate_pct || 0}%</span>
              <span className="px-2 py-1 rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8]">Flat: ${data?.commission_flat || 5}</span>
              {data?.auto_paused && <span className="sticker sticker-magenta">PAUSED</span>}
            </div>
          </div>
          <div className="card-x card-magenta bg-[#FAF6E8] p-6">
            <div className="label-overline text-[#FF1A8C] mb-3">+ Top up via Stripe</div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[
                { id: "wallet_topup_100", label: "$100" },
                { id: "wallet_topup_250", label: "$250" },
                { id: "wallet_topup_500", label: "$500" },
              ].map((p) => (
                <button
                  key={p.id}
                  data-testid={`topup-${p.id}`}
                  onClick={() => stripeTopup(p.id)}
                  disabled={!!busyPkg}
                  className="btn-x btn-ghost !py-2 !text-sm flex-1 justify-center disabled:opacity-50"
                >
                  {busyPkg === p.id ? "…" : p.label}
                </button>
              ))}
            </div>
            <button
              data-testid="topup-billing-link"
              onClick={() => nav("/billing")}
              className="btn-x btn-primary w-full !text-sm"
            >
              <CreditCard size={14} strokeWidth={2.4} /> All billing options
            </button>
            <p className="text-[10px] font-mono-x font-bold uppercase tracking-widest mt-3 opacity-55">
              Stripe test mode · funds credit on payment confirm
            </p>
          </div>
        </div>

        <div className="card-x card-cyan bg-[#FAF6E8] p-6">
          <div className="label-overline text-[#FF1A8C] mb-4">Transactions · {data?.transactions?.length || 0}</div>
          <div className="space-y-2 max-h-[460px] overflow-auto pr-2">
            {!data?.transactions?.length && <div className="text-sm text-[#0B0F0C]/55">No transactions yet.</div>}
            {(data?.transactions || []).map((t) => (
              <div key={t.id} className="flex items-center gap-3 rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8] p-3">
                <div className={`w-9 h-9 rounded-full border-2 border-[#0B0F0C] flex items-center justify-center ${t.amount > 0 ? "bg-[#15FF73]" : "bg-[#FF1A8C] text-white"}`}>
                  {t.amount > 0 ? <ArrowDown size={14} strokeWidth={2.4} /> : <ArrowUp size={14} strokeWidth={2.4} />}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold">{t.description}</div>
                  <div className="text-[10px] text-[#0B0F0C]/50 font-mono-x font-bold uppercase tracking-widest">{new Date(t.created_at).toLocaleString()}</div>
                </div>
                <div className={`font-mono-x font-bold ${t.amount > 0 ? "text-[#15FF73]" : "text-[#FF1A8C]"}`}>{t.amount > 0 ? "+" : ""}${t.amount.toFixed(2)}</div>
                <div className="font-mono-x text-xs opacity-60">→ ${t.balance_after.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
